import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, MapPin, Zap, Scale, Coffee } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
  Input,
} from "@tabipla/ui";

export interface GenerateContext {
  departureLocation?: string;
  travelStyle: "relaxed" | "active" | "balanced";
  additionalNotes?: string;
}

interface AiGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (context: GenerateContext) => Promise<void>;
  isGenerating: boolean;
}

const TRAVEL_STYLES = [
  {
    value: "relaxed" as const,
    icon: Coffee,
    labelKey: "trips.aiGenerate.relaxed",
    descKey: "trips.aiGenerate.relaxedDesc",
  },
  {
    value: "balanced" as const,
    icon: Scale,
    labelKey: "trips.aiGenerate.balanced",
    descKey: "trips.aiGenerate.balancedDesc",
  },
  {
    value: "active" as const,
    icon: Zap,
    labelKey: "trips.aiGenerate.active",
    descKey: "trips.aiGenerate.activeDesc",
  },
];

export function AiGenerateDialog({
  open,
  onOpenChange,
  onGenerate,
  isGenerating,
}: AiGenerateDialogProps) {
  const { t } = useTranslation();
  const [departureLocation, setDepartureLocation] = useState("");
  const [travelStyle, setTravelStyle] = useState<"relaxed" | "active" | "balanced">("balanced");
  const [additionalNotes, setAdditionalNotes] = useState("");

  const handleGenerate = async () => {
    await onGenerate({
      departureLocation: departureLocation.trim() || undefined,
      travelStyle,
      additionalNotes: additionalNotes.trim() || undefined,
    });
  };

  const handleClose = () => {
    if (!isGenerating) {
      onOpenChange(false);
    }
  };

  // Reset form when dialog opens, prevent closing during generation
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && isGenerating) {
      return; // Prevent closing during generation
    }
    if (newOpen) {
      setDepartureLocation("");
      setTravelStyle("balanced");
      setAdditionalNotes("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        hideCloseButton={isGenerating}
        onPointerDownOutside={(e) => {
          if (isGenerating) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isGenerating) e.preventDefault();
        }}
      >
        {isGenerating ? (
          // 生成中の表示
          <div className="flex flex-col items-center justify-center py-8">
            <div className="mb-6">
              <img src="/images/ai-generating.svg" alt="" className="h-40 w-40 object-contain" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">{t("trips.aiGenerate.generating")}</h3>
            <p className="text-center text-sm text-muted-foreground">
              {t("trips.aiGenerate.generatingDesc")}
            </p>
            <div className="mt-4 flex items-center gap-1">
              <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-primary" />
            </div>
          </div>
        ) : (
          // 入力フォーム
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {t("trips.aiGenerate.title")}
              </DialogTitle>
              <DialogDescription>{t("trips.aiGenerate.subtitle")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-4">
              {/* 出発地 */}
              <div className="space-y-2">
                <label htmlFor="departure-location" className="text-sm font-medium">
                  {t("trips.aiGenerate.departureLocation")}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="departure-location"
                    value={departureLocation}
                    onChange={(e) => setDepartureLocation(e.target.value)}
                    placeholder={t("trips.aiGenerate.departureLocationPlaceholder")}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("trips.aiGenerate.departureLocationHint")}
                </p>
              </div>

              {/* 旅行スタイル */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("trips.aiGenerate.travelStyle")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {TRAVEL_STYLES.map((style) => {
                    const Icon = style.icon;
                    const isSelected = travelStyle === style.value;
                    return (
                      <button
                        key={style.value}
                        type="button"
                        onClick={() => setTravelStyle(style.value)}
                        className={`flex flex-col items-center gap-1 rounded-lg border p-3 transition-colors active:scale-[0.98] ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary dark:bg-primary/20"
                            : "border-border hover:border-primary/50 hover:bg-primary/5 active:bg-primary/10 dark:hover:bg-primary/10"
                        }`}
                      >
                        <Icon className={`h-5 w-5 ${isSelected ? "text-primary" : ""}`} />
                        <span className="text-xs font-medium">{t(style.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  {t(TRAVEL_STYLES.find((s) => s.value === travelStyle)?.descKey ?? "")}
                </p>
              </div>

              {/* その他の希望 */}
              <div className="space-y-2">
                <label htmlFor="additional-notes" className="text-sm font-medium">
                  {t("trips.aiGenerate.additionalNotes")}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </label>
                <Textarea
                  id="additional-notes"
                  value={additionalNotes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setAdditionalNotes(e.target.value)
                  }
                  placeholder={t("trips.aiGenerate.additionalNotesPlaceholder")}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-right text-xs text-muted-foreground">
                  {additionalNotes.length}/500
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                {t("common.cancel")}
              </Button>
              <Button type="button" onClick={handleGenerate} className="flex-1">
                <Sparkles className="mr-2 h-4 w-4" />
                {t("trips.aiGenerate.startGenerate")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
